import React, { ComponentType } from "react";
import { useController } from "react-hook-form";
import { useSmartFormContext } from "components/SmartForm";
import { HTMLFormControlType, HTMLInputType } from "@mui-styled-components/SmartFormElements/types";

export type SmartFormChangeEventT =
  | React.ChangeEvent<HTMLInputElement>
  | React.ChangeEvent<HTMLSelectElement>
  | React.ChangeEvent<HTMLTextAreaElement>;

type SmartFormElementT<I extends HTMLInputType | HTMLFormControlType> = {
  name: string;
  smartElement?: boolean;
  transform?: I extends
    | HTMLInputType.Text
    | HTMLInputType.Number
    | HTMLFormControlType.Select
    | HTMLFormControlType.TextArea
    ? {
        input: (value: unknown) => unknown;
        output: (e: SmartFormChangeEventT) => unknown;
      }
    : undefined;
};

type HocOnlyPropKeysT = "smartElement" | "transform";

export const hocSmartFormElement =
  <I extends HTMLInputType, T extends object, P extends SmartFormElementT<I> = T & SmartFormElementT<I>>(
    Component: ComponentType<T> & ComponentType<Omit<P, HocOnlyPropKeysT>>,
  ) =>
  (props: P) => {
    const WrappedComponent: ComponentType<Omit<P, HocOnlyPropKeysT>> = Component;
    const { smartElement, transform, ...rest } = props;
    if (smartElement) {
      const { controllerProps } = useSmartFormContext();
      const { field } = useController({ ...controllerProps, name: props.name });

      const { onChange, onBlur, value, name: fieldname } = field;
      const processedValue = transform ? transform.input(value) : value;

      const handleOnChange = transform ? (e: SmartFormChangeEventT) => onChange(transform.output(e)) : onChange;

      const fieldWithoutRef = { onChange: handleOnChange, onBlur, value: processedValue, name: fieldname };

      return React.createElement(WrappedComponent, { ...rest, ...fieldWithoutRef });
    }
    return React.createElement(WrappedComponent, rest);
  };
